<script lang="ts">
	import * as Yup from "yup";
	import { useValidations } from "../ursCommon/Forms.svelte";
	import {
		Icon,
		Input,
		Label,
		Button,
		Card,
		CardHeader,
		CardBody,
		CardText,
		Styles,
	} from "@sveltestrap/sveltestrap";

	const { validateField, validateForm, getErrors, resetErrors, removeError } =
		useValidations();

	let name = "attachments";
	let accept = ".jpeg,.jpg,.png,.pdf";
	let multiple = true;
	let errors = $derived(getErrors());
	let formHasErrors = $derived(Object.keys(errors).length ? true : false);

	// DOM reference to the native <input type="file"> (from your <Input> wrapper)
	//let fileInput: HTMLInputElement | null = null;
	let fileInput: HTMLInputElement | null = null;

	// The current list of files the user has selected (and not removed)
	let selectedFiles: File[] = $state([]);
	let { disabled = true } = $props();
	let disabledClass = $derived(disabled ? "disabled" : "");

	// Utility: human-readable size (e.g., "2.8 MB")
	const humanFileSize = (size: number) => {
		const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
		const value = (size / Math.pow(1024, i)).toFixed(2);
		const units = ["B", "KB", "MB", "GB", "TB"];
		return `${value} ${units[i]}`;
	};

	// Build a stable key for deduping across multiple selections
	const fileKey = (f: File) => `${f.name}-${f.size}-${f.lastModified}`;

	const formSchema = Yup.object({
		selectedFiles: Yup.array()
			.ensure()
			.min(1, "Please upload Laboratory / Clinical Documentation")
			.required("Please upload Laboratory / Clinical Documentation")
			.of(
				Yup.mixed().test(
					"is-file",
					"Each item must be a File",
					(v) => v instanceof File,
				),
			)
			.test(
				"file-type",
				"Only JPG, PNG, or PDF allowed",
				(files?: File[]) =>
					Array.isArray(files) &&
					files.every((f) =>
						["image/jpeg", "image/png", "application/pdf"].includes(f.type),
					),
			),
	});

	export function clearAllErrors() {
		resetErrors();
	}

	export function getValues() {
		return selectedFiles;
	}

	export function setValues(data) {
		selectedFiles = data;
		resetErrors();
	}

	export function hasErrors() {
		return formHasErrors;
	}

	export async function validate() {
		await validateForm(formSchema, { selectedFiles });
	}

	// Handle new files chosen from the dialog
	function onFileChange(e: Event) {
		const input = e.target as HTMLInputElement;
		const files = input.files ? Array.from(input.files) : [];
		// Merge without duplicates (by name+size+lastModified)
		const existing = new Set(selectedFiles.map(fileKey));
		const merged: File[] = [...selectedFiles];

		for (const f of files) {
			const key = fileKey(f);
			if (!existing.has(key)) merged.push(f);
		}
		selectedFiles = merged;
		input.value = "";
		validate();
	}

	// Remove a single file by index
	function removeFile(e, index: number) {
		selectedFiles.splice(index, 1);
	}

	// Clear everything
	function removeAll() {
		selectedFiles = [];
		//if (fileInput) fileInput.value = ""; // reset the native input
	}

	function revokeOnLoad(e: Event & { currentTarget: HTMLImageElement }) {
		URL.revokeObjectURL(e.currentTarget.src);
	}
</script>

<Styles />
<div class="row">
	<div class="col-sm-12">
		<!-- If your <Input> component forwards bind:this and on:change to the native <input>, this works. -->
		<Label
			for="fileUploader"
			class="btn btn-primary btn-lg me-3 {disabledClass}"
		>
			Upload Laboratory / Clinical Documentation
		</Label>
		{#if errors.selectedFiles}
			<p class="radioButtonInvalid">{errors.selectedFiles}</p>
		{/if}
		<Input
			id="fileUploader"
			type="file"
			{name}
			{multiple}
			{accept}
			class="hidden"
			on:change={onFileChange}
			{disabled}
		/>
	</div>
</div>

{#if selectedFiles.length > 0}
	<div class="mt-3">
		<div class="d-flex align-items-center justify-content-between mb-2">
			<h6 class="m-0">Selected files ({selectedFiles.length})</h6>
			<Button
				class="btn btn-secondary btn-sm me-3"
				type="button"
				on:click={removeAll}
				{disabled}
			>
				Remove all
			</Button>
		</div>

		<ul class="list-group">
			{#each selectedFiles as file, i}
				<li
					class="list-group-item d-flex align-items-center justify-content-between"
				>
					<div class="d-flex align-items-center gap-3">
						<!-- Image preview for jpeg/jpg/png -->
						{#if file.type.startsWith("image/")}
							<!-- Create a temporary URL; revoke after image loads -->
							{#key fileKey(file)}
								<img
									src={URL.createObjectURL(file)}
									alt={file.name}
									width="48"
									height="48"
									style="object-fit: cover; border-radius: 4px; border: 1px solid #eee;"
									on:load={revokeOnLoad}
								/>
							{/key}
						{:else}
							<!-- Simple icon for non-images (you can swap for your icon set) -->
							<Icon name="file-earmark-pdf" />
							<!-- <Icon name="file-earmark-pdf-fill" /> -->
						{/if}

						<div class="d-flex flex-column">
							<strong class="text-truncate" style="max-width: 320px;"
								>{file.name}</strong
							>
							<small class="text-muted"
								>{file.type || "unknown type"} &middot; {humanFileSize(
									file.size,
								)}</small
							>
						</div>
					</div>

					<Button
						type="button"
						class="btn btn-secondary btn-sm me-3"
						aria-label={`Remove ${file.name}`}
						on:click={(e) => removeFile(e.target, i)}
						{disabled}
					>
						<Icon name="trash3" /> Remove
					</Button>
				</li>
			{/each}
		</ul>
	</div>
{/if}

<!-- <style>
  /* Optional: minor polish if you're using Bootstrap */
  .list-group-item {
    padding: 0.75rem 1rem;
  }
</style> -->

<style>
	/* Prevent hover from changing appearance on .btn-link */
	.btn.btn-link.no-hover:hover {
		color: var(--bs-btn-color); /* keep same as the non-hover color */
		text-decoration: none; /* Bootstrap adds underline on hover; disable it */
		background-color: transparent; /* .btn-link is transparent by default */
		border-color: transparent; /* .btn-link has no border; keep it that way */
	}
</style>
